import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SubscriptionsListView from "../../components/subscriptions/subscriptions-list";
import CurrentPlanView from "../../components/subscriptions/current-plan";
import Menu from "../../pages/menu/menu";
import {
  Box, CircularProgress, Typography, Button,
  Snackbar
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllPlans, userDetails, createPaymentSession,
  updatePaymentStatus, getUserPackage, getUserActivity,
  getUserPayments, cancelSubscription, resetCancelSubscription,
  resetPaymentSession,
  getActivePlans
} from "../../actions";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { DoneOutlineOutlined, Close, Restore } from "@material-ui/icons";
import mixpanel from 'mixpanel-browser';
import { asArray } from "../../utils/list";
import ReferralSharePrompt from "../../components/referral-share-prompt/ReferralSharePrompt";
import { configureRevenueCat, isNativeCapacitorRuntime } from "../../services/revenueCatService";
import {
  isMobileDevPaywallEnabled,
  MOBILE_BILLING_REASONS,
  MOBILE_DEV_PAYWALL_ABSITE_PLAN,
  MOBILE_DEV_PAYWALL_USER,
  buildAbsiteMobilePaywallPlans
} from "../../constants/mobileBilling";
import {
  purchaseAbsiteMobileSubscription,
  restoreAbsiteMobilePurchases
} from "../../services/mobileBillingService";
import {
  trackMobilePaywallViewed,
  trackMobilePurchaseInitiated,
  trackMobilePurchaseFailed,
  trackMobilePurchaseOutcome,
  trackMobileRestoreInitiated,
  trackMobileRestoreFailed,
  trackMobileRestoreOutcome
} from "../../services/mobileFunnelAnalytics";
import { trackPaywallViewed } from "../../utils/paywall-analytics";

const MOBILE_BILLING_SYNC_REFRESH_DELAYS_MS = [3000, 10000, 30000];

// Module scope so both the paywall-view guard and the data-fetch effect below
// resolve to the same predicate rather than two hand-copied conditions.
const hasUserId = (id) => Boolean(id && id !== "undefined");

export default function SubscriptionsView() {
  const [paymentStatus, setPaymentStatus] = useState('');
  const [activePackage, setActivePackage] = useState({});
  const [showPlan, setShowPlan] = useState(null);
  const [userActivityDetails, setUserActivityDetails] = useState({});
  const [open, setOpen] = useState(false);
  const [showPlanSuccessAlt, setShowPlanSuccessAlt] = useState(false);
  const [onPaymentLoadSuccess, setOnPaymentLoadSuccess] = useState(false);
  const [emailNotify, setEmailNotify] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  // The shared snackbar's 2s default is unreadable for the long actionable
  // billing messages (timeout recovery instructions) — those set 10s.
  const [snackbarDuration, setSnackbarDuration] = useState(2000);
  const [showPaymentIframe] = useState(false);
  const [iframeUrl] = useState('');
  const [restorePurchasesLoading, setRestorePurchasesLoading] = useState(false);
  // B5: explicit, persistent post-purchase success state (not just a 2s snackbar).
  const [mobilePurchaseSuccess, setMobilePurchaseSuccess] = useState(false);

  const dispatch = useDispatch();
  let { bookingId } = useParams();
  let location = useLocation();
  const history = useHistory();
  const theme = useTheme();

  const userData = useSelector((state) => {
    return state.studentDetails.userDetails;
  });
  const localUserId = window.localStorage.getItem("userId");
  const isMobileDevPaywall = isMobileDevPaywallEnabled();
  const effectiveUserData = useMemo(() => {
    if (!isMobileDevPaywall) {
      return userData;
    }

    return {
      ...MOBILE_DEV_PAYWALL_USER,
      ...userData,
      id: userData?.id || (localUserId && localUserId !== "undefined" ? localUserId : MOBILE_DEV_PAYWALL_USER.id),
      email: userData?.email || MOBILE_DEV_PAYWALL_USER.email
    };
  }, [isMobileDevPaywall, localUserId, userData]);
  const revenueCatUserId = effectiveUserData?.id || localUserId;

  const packageListRes = useSelector((state) => {
    return state.getAllPlans;
  });

  const paymentSession = useSelector((state) => {
    return state.paymentSession?.paymentSession;
  });

  const paymentStatusRes = useSelector((state) => {
    return state.paymentStatus?.paymentUpdateStatus;
  });

  const userPackage = useSelector((state) => {
    return state.userPackage?.userPackage;
  });

  const userActivity = useSelector((state) => {
    return state.userActivity?.userActivity;
  });

  const paymentHistory = useSelector((state) => {
    return state.userPayments?.paymentHistory;
  });

  const cancelSubscriptionReq = useSelector((state) => {
    return state.cancelSubscription?.cancelSubscription;
  });

  const activePlans = useSelector((state) => {
    return state.getActivePlans;
  });

  // B3: a failed checkout round-trips through route state carrying the plan the
  // user was buying, so the list can reopen the same checkout.
  // The daily-limit "Try Pro free" button sends its trial plan the same way
  // (preselectPlanId), so the student lands on that plan's review step. Both
  // are single-use: onRetryConsumed strips them.
  const retryPlanId = location.state?.retryPlanId ?? location.state?.preselectPlanId;

  // The retry state is single-use: the moment the list consumes it, strip it
  // from the history entry so refresh/back-forward navigation cannot replay
  // the forced-open checkout.
  const onRetryConsumed = useCallback(() => {
    history.replace({ pathname: "/subscriptions" });
  }, [history]);

  // Surface a gateway decline reason ONLY when the failure payload provides
  // one. NOTE: this route is ALSO Stripe Checkout's cancel_url
  // (StripeSubscriptionService setCancelUrl) — most arrivals here CANCELLED
  // rather than failed a card, and the booking-status FAILED response carries
  // no decline field today (API gap). So the default copy stays neutral and
  // makes no claim about a decline; a real reason renders automatically if
  // the backend ever supplies one.
  const failureDetail = paymentStatusRes?.failureReason
    || paymentStatusRes?.declineMessage
    || paymentStatusRes?.failureMessage;

  const activePlansList = useMemo(() => asArray(activePlans?.plansList), [activePlans?.plansList]);
  const apiAvailablePlans = useMemo(() => asArray(packageListRes?.plans), [packageListRes?.plans]);
  const availablePlans = useMemo(() => {
    if (isMobileDevPaywall) {
      return [MOBILE_DEV_PAYWALL_ABSITE_PLAN];
    }

    // Native (non-dev): present the single purchasable ABSITE product with its
    // App Store trial, mirroring the dev-preview paywall, instead of the raw
    // multi-plan web catalogue that rendered without the trial card. Web keeps
    // the full plan list unchanged.
    if (isNativeCapacitorRuntime()) {
      return buildAbsiteMobilePaywallPlans(apiAvailablePlans);
    }

    return apiAvailablePlans;
  }, [apiAvailablePlans, isMobileDevPaywall]);
  const userPackages = useMemo(() => asArray(userPackage), [userPackage]);
  const paymentHistoryList = useMemo(() => asArray(paymentHistory), [paymentHistory]);
  const safeActivePlans = useMemo(() => ({
    ...activePlans,
    plansList: activePlansList
  }), [activePlans, activePlansList]);
  const isNativeBillingRuntime = isNativeCapacitorRuntime() || isMobileDevPaywall;

  // Get the active plan ID - check for selected, non-expired subscriptions with valid end dates.
  // The backend can return status=ACTIVE and expired=true together for lapsed rows;
  // in that case the expired flag must win so the renewal/purchase row stays visible.
  const activePlanId = activePlansList.filter(item => {
    const isActive = item.status === 'ACTIVE';
    const isExpired = item.expired === true;
    const hasValidEndDate = new Date(item.subscriptionEndDt) >= new Date();
    const isSelected = item.iselected;
    return isActive && !isExpired && hasValidEndDate && isSelected;
  })[0]?.plan?.id;

  // The plans the student is actually OFFERED. Extracted rather than inlined at
  // the <SubscriptionsListView/> call site because the Paywall Viewed event
  // reports its length: one expression, used by the UI and by the measurement,
  // so the number in the warehouse cannot drift from what was on screen.
  const purchasablePlans = useMemo(
    () => availablePlans.filter(plan => plan.id !== activePlanId),
    [availablePlans, activePlanId]
  );

  /*
   * Paywall Viewed — the conversion DENOMINATOR (see utils/paywall-analytics).
   *
   * Fires once per mount, on the first render where the paywall is actually on
   * screen. Each guard below is load-bearing:
   *
   *   isNativeBillingRuntime — iOS already fires Mobile Paywall Viewed from the
   *     effect a few lines down. Firing both would double-count every native
   *     view into the web funnel.
   *   bookingId / showPaymentIframe — /payment/success/:bookingId and
   *     /payment/failure/:bookingId mount THIS component, and the paywall block
   *     is behind `!bookingId && !showPaymentIframe`. A return from checkout is
   *     not a paywall view; counting it would put students who already
   *     converted into the denominator. (showPaymentIframe has no setter today —
   *     both call sites are commented out — so it is permanently false and that
   *     half of the guard is currently inert. It is kept because this condition
   *     must mirror the render condition exactly; the day the iframe is
   *     re-enabled, the event is already right rather than newly wrong.)
   *   loading !== false — SETTLED, not merely not-loading. See below.
   *
   * The ref latch, not a module-level flag: one event per page view is the
   * right grain for a denominator (uniques are Mixpanel's job, on distinct_id),
   * and module state would silently suppress the second view in a session.
   *
   * `!== false` IS THE WHOLE FIX, and `if (loading)` was a real defect rather
   * than a style nit. Both reducers start at `initialState = {}`, so `loading`
   * is UNDEFINED — falsy — before the request has even been dispatched. The
   * event therefore fired on the very first render of a cold load, with an
   * empty plan list, reporting purchasable_plan_count: 0. The latch then
   * blocked the corrected value forever. Since the documented formula is
   * `bought / viewed WHERE purchasable_plan_count > 0`, that silently discarded
   * the FIRST paywall view of every session while SPA re-entries (warm store)
   * reported correctly — bimodal data that reads as ordinary variance.
   *
   * BOTH requests are waited on, because the count depends on both. An earlier
   * version of this comment claimed "the rendered count has no race"; that was
   * wrong. `activePlanId` comes from getActivePlans, a SEPARATE request, so a
   * subscriber whose active plan had not landed was reported with the full
   * catalogue — the same direction of error as the `has_active_plan` boolean
   * this property was chosen over. The count is still the better field (it
   * separates "subscriber with an upgrade available" from "subscriber with
   * nothing left", which a boolean cannot); the race was about WHEN the event
   * fires, not WHICH field it carries.
   *
   * The active-plan wait is conditional on purpose. getActivePlans is dispatched
   * only for a signed-in student, and /subscriptions has NO auth gate — so for
   * an anonymous visitor that slice stays {} forever and an unconditional gate
   * would emit nothing for exactly the population most likely to be converting.
   */
  /*
   * ONE definition, shared with the dispatch site below. They were written
   * separately and must not drift: this guard waits for the active-plan request
   * precisely when that dispatch issues it, and any disagreement is a silent
   * hole in one direction or the other — an anonymous visitor never counted, or
   * a signed-in student waiting forever for a request nobody made.
   *
   * The `!== "undefined"` half is the literal STRING, not the value: this
   * codebase writes that into localStorage in at least two other places and
   * defends against it. Without it, a user carrying `userId = "undefined"` sets
   * isSignedIn true, so this effect waits for a slice that never settles —
   * while the dispatch below correctly refuses to fetch. Zero events, forever,
   * for exactly the population most likely to be converting.
   */
  const isSignedIn = hasUserId(localUserId);
  const paywallViewSent = useRef(false);
  useEffect(() => {
    if (paywallViewSent.current) return;
    if (isNativeBillingRuntime) return;
    if (bookingId || showPaymentIframe) return;
    if (packageListRes?.loading !== false) return;
    if (isSignedIn && activePlans?.loading !== false) return;

    paywallViewSent.current = true;
    trackPaywallViewed({
      route: location.pathname,
      purchasablePlanCount: purchasablePlans.length,
      // A zero can mean three different things — a subscriber with nothing left
      // to buy, a failed plans fetch, and (before the guard above) a fetch that
      // had not started. The third is gone; this separates the second, so an
      // outage cannot masquerade as "nobody had anything to buy".
      plansLoadFailed: Boolean(packageListRes?.errorMsg),
    });
  }, [
    isNativeBillingRuntime,
    bookingId,
    showPaymentIframe,
    packageListRes?.loading,
    packageListRes?.errorMsg,
    isSignedIn,
    activePlans?.loading,
    location.pathname,
    purchasablePlans,
  ]);

  useEffect(() => {
    if (bookingId) {
      const isSuccess = location.pathname.search('success') > -1;
      const status = isSuccess ? 'success' : 'failure';
      setPaymentStatus(status);
      dispatch(updatePaymentStatus({
        isSuccess,
        bookingId
      }));

      if (isSuccess) {
        mixpanel.track('Subscription bought', {
          packageName: activePackage?.packages?.name
        });
      }
    }

    if (!isMobileDevPaywall && !packageListRes.plans) {
      dispatch(getAllPlans());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Mobile funnel: the paywall being shown is the top of the conversion funnel.
  // Fire once per mount on the native billing runtime (mobile has no other
  // paywall-view event today). Suppressed under the dev paywall so a local
  // preview build doesn't pollute the shared-writeKey warehouse (matches the
  // dev-paywall suppression in the purchase/restore terminal events).
  useEffect(() => {
    if (isNativeBillingRuntime && !isMobileDevPaywall) {
      trackMobilePaywallViewed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobileDevPaywall) {
      return;
    }

    const savedUserId = window.localStorage.getItem("userId");

    if (hasUserId(savedUserId)) {
      if (!userData) {
        dispatch(userDetails(savedUserId));
      }
      dispatch(getUserPackage(savedUserId));
      dispatch(getUserActivity(savedUserId));
      dispatch(getUserPayments(savedUserId));
      dispatch(getActivePlans(savedUserId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isMobileDevPaywall) {
      return;
    }

    if (revenueCatUserId && revenueCatUserId !== "undefined") {
      configureRevenueCat({ appUserID: revenueCatUserId });
    }
  }, [isMobileDevPaywall, revenueCatUserId]);

  useEffect(() => {
    if (paymentSession && paymentSession.checkoutURL) {
      window.open(paymentSession.checkoutURL, '_self');
      // setIframeUrl(paymentSession.checkoutURL);
      setOnPaymentLoadSuccess(true);
      // setShowPaymentIframe(true);
      dispatch(resetPaymentSession());

      mixpanel.track('Subscription payment url opened', {
        packageName: activePackage?.packages?.name
      });
    } else if (paymentSession && paymentSession.booking?.id) {
      setTimeout(() => { dispatch(getUserPayments(savedUserId)) }, 2000);
      // setShowPaymentIframe(false);
      history.push('/subscriptions');
      setOpen(true);
      setEmailNotify(true);
      setOnPaymentLoadSuccess(true);

      const savedUserId = window.localStorage.getItem("userId");
      dispatch(getUserPackage(savedUserId));
      dispatch(resetPaymentSession());

      mixpanel.track('Subscription requested', {
        packageName: activePackage?.packages?.name
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, paymentSession]);

  useEffect(() => {
    if (userPackage) {
      const activePkg = userPackages.length === 1 ? userPackages[0] :
        userPackages.filter(item => item.status === 'ACTIVE')[0];

      setActivePackage(activePkg || {});
    }
  }, [userPackage, userPackages]);

  useEffect(() => {
    if (userActivity) {
      setUserActivityDetails(userActivity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userActivity]);

  useEffect(() => {
    if (cancelSubscriptionReq) {
      setSnackbarMessage('Your subscription has been cancelled successfully...');
      setOpen(true);
      dispatch(resetCancelSubscription());
      const savedUserId = window.localStorage.getItem("userId");
      dispatch(getUserPackage(savedUserId));
      dispatch(getUserPayments(savedUserId));
      dispatch(getActivePlans(savedUserId));

      mixpanel.track('Subscription cancelled', {
        packageName: activePackage?.packages?.name
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelSubscriptionReq]);

  const refreshBillingData = (userId) => {
    if (userId && userId !== "undefined") {
      dispatch(getUserPackage(userId));
      dispatch(getUserPayments(userId));
      dispatch(getActivePlans(userId));
    }
  };

  const refreshBillingDataAfterMobileSync = (userId) => {
    refreshBillingData(userId);

    MOBILE_BILLING_SYNC_REFRESH_DELAYS_MS.forEach((delayMs) => {
      setTimeout(() => refreshBillingData(userId), delayMs);
    });
  };

  const getMobileBillingMessage = (result) => {
    if (result?.purchased) {
      return 'Purchase complete. Access may take a moment to update.';
    }

    if (result?.reason === 'ACTIVE_ACCESS') {
      return 'You already have active access.';
    }

    if (result?.reason === MOBILE_BILLING_REASONS.DEV_PAYWALL_STORE_DISABLED) {
      return 'Store purchases are disabled in this local iOS preview build.';
    }

    if (result?.reason === MOBILE_BILLING_REASONS.SETUP_TIMED_OUT) {
      // A pre-sheet dependency hung — nothing was purchased; retry is safe.
      return 'The store is taking too long to respond. Check your connection and try again.';
    }

    if (result?.reason === MOBILE_BILLING_REASONS.PURCHASE_TIMED_OUT) {
      // The Apple sheet never appeared (or was never completed) — tell the
      // user something actionable instead of spinning forever.
      return 'The purchase didn\'t start. Make sure you\'re signed in to the App Store, then try again — if you did complete it, use Restore Purchases.';
    }

    return 'Unable to start mobile purchase. Please try again.';
  };

  const getMobileRestoreMessage = (result) => {
    if (result?.restored) {
      return 'Purchases restored. Access may take a moment to update.';
    }

    // B2: restore resolved but there was nothing to restore — say so plainly
    // instead of implying something was restored.
    if (result?.nothingToRestore || result?.reason === MOBILE_BILLING_REASONS.NOTHING_TO_RESTORE) {
      return 'No purchases found to restore.';
    }

    if (result?.reason === MOBILE_BILLING_REASONS.DEV_PAYWALL_STORE_DISABLED) {
      return 'Restore purchases is disabled in this local iOS preview build.';
    }

    if (result?.reason === MOBILE_BILLING_REASONS.RESTORE_TIMED_OUT) {
      return 'Restore didn\'t finish — check your connection and try again.';
    }

    return 'Unable to restore purchases. Please try again.';
  };

  const handleMobileConfirmation = async (data) => {
    const packageName = activePackage?.packages?.name;
    // Mobile funnel: user tapped Subscribe and we're about to open the Apple
    // purchase sheet. Suppressed under the dev paywall (no real purchase, and
    // the terminal events are suppressed too — keep the funnel consistent).
    if (!isMobileDevPaywall) {
      trackMobilePurchaseInitiated({ packageName });
    }

    // GPT-5.6 finding 1: never let the purchase default silently — the sheet
    // must buy exactly the StoreKit product the presented row was bound to.
    if (!data.revenueCatProductId) {
      trackMobilePurchaseFailed({ stage: 'unbound-plan', packageName });
      setSnackbarMessage('This plan is temporarily unavailable for purchase in the app. Please try again later.');
      setSnackbarDuration(6000);
      setOpen(true);
      return { purchased: false, stage: 'unbound-plan', mobileBilling: true };
    }

    try {
      const result = await purchaseAbsiteMobileSubscription({
        appUserID: data.userId,
        productId: data.revenueCatProductId,
        // A slow-but-real sheet (password entry, Ask to Buy, SCA) can complete
        // AFTER the watchdog already reported a timeout — the user was charged,
        // so flip to the success state instead of leaving a failure message up.
        onLateCompletion: () => {
          setMobilePurchaseSuccess(true);
          refreshBillingDataAfterMobileSync(data.userId);
          setSnackbarMessage('Purchase complete. Access may take a moment to update.');
          setSnackbarDuration(4000);
          setOpen(true);
          window.analytics?.track?.('Subscription bought', { packageName, provider: 'REVENUECAT' });
          mixpanel.track('Subscription bought', { packageName, provider: 'REVENUECAT' });
        }
      });
      // Mobile funnel: fire the terminal event (cancelled/failed) for this
      // outcome. Success is left to the existing `Subscription bought` event.
      trackMobilePurchaseOutcome(result, { packageName });
      const resultWithSource = { ...result, mobileBilling: true };

      // B1: a user cancel is a normal action — show no error, no snackbar.
      if (result.cancelled) {
        setEmailNotify(false);
        return resultWithSource;
      }

      setSnackbarMessage(getMobileBillingMessage(result));
      setSnackbarDuration(result?.reason === MOBILE_BILLING_REASONS.PURCHASE_TIMED_OUT ? 10000 : 2000);
      setOpen(true);
      setEmailNotify(false);

      if (result.purchased) {
        // B5: transition the checkout screen to an explicit success state.
        setMobilePurchaseSuccess(true);
        refreshBillingDataAfterMobileSync(data.userId);
        window.analytics?.track?.('Subscription bought', {
          packageName: activePackage?.packages?.name,
          provider: 'REVENUECAT'
        });
        mixpanel.track('Subscription bought', {
          packageName: activePackage?.packages?.name,
          provider: 'REVENUECAT'
        });
      }

      return resultWithSource;
    } catch (error) {
      // Mobile funnel: an unexpected throw is also a purchase failure.
      trackMobilePurchaseFailed({ packageName, stage: 'exception' });
      setSnackbarMessage('Unable to start mobile purchase. Please try again.');
      setOpen(true);
      setEmailNotify(false);

      return {
        mobileBilling: true,
        purchased: false,
        error
      };
    }
  };

  const onConfirmation = (data) => {
    if (isNativeBillingRuntime) {
      return handleMobileConfirmation(data);
    }

    dispatch(createPaymentSession(data));
  }

  const handleRestorePurchases = async () => {
    setRestorePurchasesLoading(true);
    // Mobile funnel: user tapped Restore Purchases (suppressed under dev paywall).
    if (!isMobileDevPaywall) {
      trackMobileRestoreInitiated();
    }

    try {
      const result = await restoreAbsiteMobilePurchases({ appUserID: revenueCatUserId });
      // Mobile funnel: fire the terminal event (empty/failed) for this outcome.
      // Success is left to the existing `Subscription restored` event.
      trackMobileRestoreOutcome(result);

      setSnackbarMessage(getMobileRestoreMessage(result));
      // Duration discipline (gate P2): the restore-timeout instructions need
      // the long duration too, and a stale 10s from a prior purchase timeout
      // must not leak into short restore messages.
      setSnackbarDuration(result?.reason === MOBILE_BILLING_REASONS.RESTORE_TIMED_OUT ? 10000 : 2000);
      setOpen(true);
      setEmailNotify(false);

      if (result.restored) {
        refreshBillingDataAfterMobileSync(revenueCatUserId);
        window.analytics?.track?.('Subscription restored', {
          provider: 'REVENUECAT'
        });
        mixpanel.track('Subscription restored', {
          provider: 'REVENUECAT'
        });
      }
    } catch (error) {
      // Mobile funnel: an unexpected throw during restore.
      trackMobileRestoreFailed({ stage: 'exception' });
      setSnackbarMessage('Unable to restore purchases. Please try again.');
      setOpen(true);
      setEmailNotify(false);
    } finally {
      setRestorePurchasesLoading(false);
    }
  };

  const subcriptionEndConfirm = (subscriptionId) => {
    const payment = paymentHistoryList.filter(item => item.subscriptionId === subscriptionId);
    if (payment && payment.length) {
      dispatch(cancelSubscription(payment[0].id));
    }
  }

  const closeStatus = () => {
    if (paymentStatusRes && paymentStatus === 'success') {
      setShowPlanSuccessAlt(true);
      history.push("/subscriptions");
      return;
    }
    if (paymentStatus === 'failure') {
      // B3: carry the plan the user was buying back to the list so "Try Again"
      // reopens the SAME checkout instead of resetting to an empty selection.
      history.push({
        pathname: "/subscriptions",
        state: { retryPlanId: paymentStatusRes?.plan?.id }
      });
      return;
    }
    history.push("/subscriptions");
  }

  const handleClose = () => {
    setOpen(false);
    setEmailNotify(false);
    setSnackbarMessage('');
  };

  const handlePlanSuccessAltClose = () => {
    setShowPlanSuccessAlt(false);
    setEmailNotify(false);
  };

  const showPlanDetails = (planObj) => {
    setShowPlan(planObj.plan);
  };

  const hidePlanDetailsModal = () => {
    setShowPlan(null);
  };

  const useStyles = makeStyles(() => ({
    examContainer: {
      display: "flex"
    },
    loaderCont: {
      position: 'absolute',
      left: '50%',
      top: '42%'
    },
    statusCont: {
      display: "flex",
      justifyContent: 'center',
      marginTop: 200,
      flex: 1
    },
    statusContWrapper: {
      boxShadow: '1px 5px 5px var(--border-color)',
      height: 310,
      padding: 20,
      width: 500,
      textAlign: 'center',
      border: '1px solid var(--border-color)',
      [theme.breakpoints.down(600)]: {
        width: 'auto'
      }
    },
    icon: {
      background: '#5cb85c',
      padding: 10,
      borderRadius: 40,
      color: '#fff',
      marginBottom: 5
    },
    iconRed: {
      background: 'red',
      padding: 10,
      borderRadius: 40,
      color: '#fff',
      marginBottom: 5
    },
    header: {
      textAlign: 'center',
      marginBottom: 20
    },
    item: {
      lineHeight: '35px',
      textAlign: 'left'
    },
    itemText: {
      float: 'right',
      fontWeight: 'bold'
    },
    btn: {
      marginTop: 20
    },
    plansHeader: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 20,
      [theme.breakpoints.down(600)]: {
        alignItems: 'stretch',
        flexDirection: 'column'
      }
    }
  }));

  const classes = useStyles();

  return (
    <Box component="div" className={`body-cont ${isNativeBillingRuntime ? 'subscriptions-body-mobile' : ''}`}>
      <Snackbar open={open} onClose={handleClose} autoHideDuration={snackbarDuration}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>

        <Box className="success-alert">
          {snackbarMessage &&
            <>{snackbarMessage}</>
          }
          {!snackbarMessage && !emailNotify &&
            <>Your subscription has been cancelled successfully...</>
          }
          {!snackbarMessage && emailNotify &&
            <>An email has been sent to your registered mail id with all the details.
              Your payment will be auto-debited.</>
          }
        </Box>
      </Snackbar>
      <Snackbar open={showPlanSuccessAlt} onClose={handlePlanSuccessAltClose} autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box className="success-alert">
          Congratulations! {paymentStatusRes?.plan?.name} is now active. If you want to switch to another plan,
          you can select from the Active Plans below.
        </Box>
      </Snackbar>
      {!isNativeBillingRuntime && <Menu />}
      {!bookingId &&
        !showPaymentIframe &&
        <>
          {packageListRes?.loading && (
            <Box className={classes.loaderCont}><CircularProgress size={24} className="loader" /></Box>
          )}

          {!packageListRes?.loading &&
            <Box className={`wd-100 subscriptions-page ${isNativeBillingRuntime ? 'subscriptions-page-mobile' : ''}`}>
              <Box className={classes.plansHeader}>
                <Typography variant="h4" className="plans-title">
                  Plans and Billing
                </Typography>
              </Box>
              {/* Reach lever 2, placement (b): the plan screen is a high-intent
                  moment — "or get Pro free by referring". Renders NOTHING today:
                  INVITE_PROMPT_CONFIG leaves "paywall" out of enabledSurfaces.
                  If it is ever added, the prompt still gates itself to free
                  users here (paywall is not a subscriber surface). No score exists
                  here, which is why lever 1's requireScore=false and the
                  score-free copy path had to land first. */}
              <ReferralSharePrompt surface="paywall" />
              {isNativeBillingRuntime && mobilePurchaseSuccess &&
                <Box
                  className="mobile-purchase-success"
                  data-testid="mobile-purchase-success"
                  role="status"
                >
                  <Typography variant="h6" className="mobile-purchase-success-title">
                    ✓ Subscription active
                  </Typography>
                  <Typography variant="body2" className="mobile-purchase-success-body">
                    Your QLearn ABSITE Pro subscription is now active. Access may take a moment to update — reopen the app if it isn't visible yet.
                  </Typography>
                </Box>
              }
              {/* Session recording (M3) shows the plan cards and prices, which
                  is what a recording of the paywall is for. What is the
                  student's own stays masked: the current plans and billing
                  history (CurrentPlanView masks its whole box, so rows added
                  later inherit it), the checkout email, and the coupon codes
                  (subscriptions-list.jsx). /payment/* is never recorded.
                  See src/utils/session-replay.js. */}
              <Box className="plans-wrapper">
                <SubscriptionsListView
                  activePackage={activePackage}
                  userActivity={userActivityDetails}
                  packageList={purchasablePlans}
                  userData={effectiveUserData}
                  isMobileBilling={isNativeBillingRuntime}
                  mobilePurchaseSuccess={mobilePurchaseSuccess}
                  onPaymentLoadSuccess={onPaymentLoadSuccess}
                  onConfirmation={onConfirmation}
                  showPlanDetailsModal={showPlan}
                  hidePlanDetailsModal={hidePlanDetailsModal}
                  isActivePlan={activePlanId}
                  retryPlanId={retryPlanId}
                  onRetryConsumed={onRetryConsumed}
                />
                <CurrentPlanView
                  activePackage={activePackage}
                  paymentHistory={paymentHistoryList}
                  activePlans={safeActivePlans}
                  userActivity={userActivityDetails}
                  subcriptionEndConfirm={subcriptionEndConfirm}
                  showPlanDetails={showPlanDetails}
                  isMobileBilling={isNativeBillingRuntime}
                />
              </Box>
              {/* Native pattern: Restore Purchases is a quiet footer action,
                  not a competing top-of-screen CTA (App Review still requires
                  it to be findable on the paywall screen). */}
              {isNativeBillingRuntime &&
                <Box className="restore-purchases-footer">
                  <Button
                    data-testid="restore-purchases"
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<Restore />}
                    disabled={restorePurchasesLoading || !revenueCatUserId}
                    onClick={handleRestorePurchases}
                  >
                    Restore Purchases
                  </Button>
                </Box>
              }
            </Box>
          }
        </>
      }

      {
        showPaymentIframe &&
        <iframe src={iframeUrl} title="Payment iframe" className="payment-iframe"></iframe>
      }

      {bookingId &&
        <>
          {paymentStatus === 'success' && paymentStatusRes &&
            <Box className={classes.statusCont}>
              <Box className={classes.statusContWrapper}>
                <Box className={classes.header}>
                  <DoneOutlineOutlined className={classes.icon} />
                  <Typography component="div" variant="h5"><b>Payment Successful</b></Typography>
                </Box>
                <Box>
                  <Typography className={classes.item} >Package name: <span className={classes.itemText}>{paymentStatusRes.plan.name}</span></Typography>
                  {/* S1 suppression fallback (leg-A verdict on PR #652): this
                      payment-result route is globally registered and mountable
                      in the native webview, where the backend dollar figure
                      can misstate an App Store charge — the amount is withheld
                      on native (StoreKit-localized-or-suppressed class rule).
                      Web/Stripe results render unchanged. */}
                  {!isNativeBillingRuntime &&
                    <Typography className={classes.item}>Amount paid: <span className={classes.itemText}>${paymentStatusRes.price}</span></Typography>
                  }
                  <Typography className={classes.item}>Transaction Id: <span className={classes.itemText}>{paymentStatusRes.id}</span></Typography>
                  <Typography className={classes.item}>Email: <span className={classes.itemText}>{paymentStatusRes.user.email}</span></Typography>
                </Box>
                <Button variant="contained" color="primary" onClick={closeStatus} className={classes.btn}>
                  Subscriptions
                </Button>
              </Box>
            </Box>
          }
          {paymentStatus === 'failure' && paymentStatusRes &&
            <Box className={classes.statusCont}>
              <Box className={classes.statusContWrapper}>
                <Box className={classes.header}>
                  <Close className={classes.iconRed} />
                  <Typography component="div" variant="h5"><b>Payment Failed</b></Typography>
                </Box>
                <Box>
                  <Typography className={classes.item} >Package name: <span className={classes.itemText}>{paymentStatusRes?.plan?.name}</span></Typography>
                  {paymentStatusRes?.paymentIntentId &&
                    <Typography className={classes.item}>Transaction Id: <span className={classes.itemText}>{paymentStatusRes?.paymentIntentId}</span></Typography>
                  }
                  <Typography className={classes.item}>Email: <span className={classes.itemText}>{paymentStatusRes?.user?.email}</span></Typography>
                  {failureDetail
                    ? <Typography className={classes.item}>Reason: <span className={classes.itemText}>{failureDetail}</span></Typography>
                    : <Typography className={classes.item}>Your payment didn't complete.</Typography>
                  }
                </Box>
                <Button variant="contained" color="primary" onClick={closeStatus} className={classes.btn}>
                  Try Again
                </Button>
              </Box>
            </Box>
          }
        </>
      }
    </Box>
  );
}
